import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bar, Line, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  RadialLinearScale
} from 'chart.js';
import { useAuth } from '../../hooks/useAuth';
import { getApiUrl } from '../../config/api';
import ResearchSessionManager from './ResearchSessionManager';
import './EnhancedAnalytics.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  RadialLinearScale
);

const EnhancedAnalytics = () => {
  // console.log('EnhancedAnalytics component loaded - updated version');
  const { user } = useAuth();
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: '2010-01-01', // Default to all available data
    end: new Date().toISOString().split('T')[0]
  });
  const [useAllTime, setUseAllTime] = useState(true); // Default to all time
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  const [queryCache] = useState(new Map());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [researchNotes, setResearchNotes] = useState('');
  
  // Add timeout ref for debouncing
  const searchTimeoutRef = useRef(null);

  // Get auth headers
  const getAuthHeaders = useCallback(async () => {
    if (!user) return {};
    
    try {
      const token = await user.getIdToken();
      // console.log('Got auth token:', token ? 'present' : 'missing');
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return {};
    }
  }, [user]);

  // Smart query execution with caching
  const executeQuery = useCallback(async (queryKey, queryFn, useBigQuery = false) => {
    const cacheKey = JSON.stringify(queryKey);
    
    // Check cache first
    if (queryCache.has(cacheKey)) {
      const cached = queryCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 3600000) { // 1 hour TTL
        return cached.data;
      }
    }

    // Cache miss - execute query
    try {
      let result;
      
      if (useBigQuery) {
        result = await queryFn();
      } else {
        result = await queryFn();
      }
      
      // Cache the result
      queryCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      console.error('Query execution failed:', error);
      throw error;
    }
  }, [queryCache]);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const headers = await getAuthHeaders();
      
      // Ensure we have valid auth headers before making requests
      if (!headers.Authorization) {
        console.error('No authorization token available');
        setError('Authentication required');
        return;
      }
      
      const overview = await executeQuery(
        ['overview'],
        () => {
          const url = `${getApiUrl('api/analytics/overview')}?_t=${Date.now()}`;
          // console.log('Making request to:', url);
          // console.log('With headers:', headers);
          return fetch(url, { headers }).then(res => {
            // console.log('Response status:', res.status);
            // console.log('Response headers:', Object.fromEntries(res.headers.entries()));
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            return res.json();
          });
        },
        false
      );
      
      const brands = await executeQuery(
        ['brands'],
        () => fetch(`${getApiUrl('api/analytics/brands')}?_t=${Date.now()}`, { headers }).then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.json();
        }),
        false
      );
      
      setData({ overview, brands });
    } catch (err) {
      setError(err.message);
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [user, executeQuery, getAuthHeaders]);

  // Search brands with debouncing
  const searchBrands = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const headers = await getAuthHeaders();
      
      // Ensure we have valid auth headers before making requests
      if (!headers.Authorization) {
        console.error('No authorization token available for brand search');
        return;
      }
      
      const response = await fetch(`${getApiUrl('api/analytics/brand-autocomplete')}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend returns { brands: [...] }, extract both brand name and domain
        const brandSuggestions = data.brands ? data.brands.map(b => ({
          displayName: b.brand,
          domain: b.domain
        })) : [];
        setSuggestions(brandSuggestions);
      }
    } catch (error) {
      console.error('Brand search failed:', error);
    }
  }, [getAuthHeaders]);

  // Load comparison data
  const loadComparisonData = useCallback(async () => {
    if (selectedBrands.length === 0 || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const headers = await getAuthHeaders();
      
      // Ensure we have valid auth headers before making requests
      if (!headers.Authorization) {
        console.error('No authorization token available for comparison data');
        setError('Authentication required');
        return;
      }
      
      const comparisonData = await executeQuery(
        ['brand-search', selectedBrands, useAllTime ? null : dateRange],
        () => fetch(`${getApiUrl('api/analytics/brand-search')}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            brands: selectedBrands, 
            dateRange: useAllTime ? null : dateRange
          })
        }).then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.json();
        }),
        true
      );
      
      setData(prev => ({ ...prev, comparison: comparisonData }));
    } catch (err) {
      setError(err.message);
      console.error('Error loading comparison data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBrands, dateRange, useAllTime, user, executeQuery, getAuthHeaders]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous timeout and set new one for debouncing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => searchBrands(value), 300);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    // console.log('handleSuggestionClick called with:', suggestion);
    const domain = suggestion.domain || suggestion; // Handle both new format and fallback
    if (!selectedBrands.includes(domain)) {
      const newBrands = [...selectedBrands, domain];
      // console.log('Adding brand, new selectedBrands:', newBrands);
      setSelectedBrands(newBrands);
    }
    setSearchQuery('');
    setSuggestions([]);
  };

  // Handle brand removal
  const handleRemoveBrand = (index) => {
    setSelectedBrands(prev => prev.filter((_, i) => i !== index));
  };

  // Handle search
  const handleSearch = () => {
    if (selectedBrands.length > 0) {
      loadComparisonData();
    }
  };

  // Handle notes
  const handleSaveNotes = () => {
    localStorage.setItem('researchNotes', researchNotes);
    alert('Notes saved!');
  };

  const handleClearNotes = () => {
    setResearchNotes('');
    localStorage.removeItem('researchNotes');
  };

  // Handle session loading
  const handleSessionLoad = useCallback((session) => {
    if (session) {
      setSelectedBrands(session.selectedBrands || []);
      setDateRange(session.dateRange || {
        start: '2010-01-01',
        end: new Date().toISOString().split('T')[0]
      });
      setUseAllTime(session.useAllTime !== undefined ? session.useAllTime : true);
      setResearchNotes(session.researchNotes || '');
      
      // Load comparison data if brands are selected
      if (session.selectedBrands && session.selectedBrands.length > 0) {
        setTimeout(() => {
          loadComparisonData();
        }, 100);
      }
    }
  }, [loadComparisonData]);

  // Handle session save
  const handleSessionSave = useCallback((session) => {
    console.log('Session saved:', session);
    // Could show a toast notification here
  }, []);

  // Load saved notes on component mount
  useEffect(() => {
    const savedNotes = localStorage.getItem('researchNotes');
    if (savedNotes) {
      setResearchNotes(savedNotes);
    }
  }, []);

  // Load initial data on mount
  useEffect(() => {
    if (user) {
      loadInitialData();
    }
  }, [user, loadInitialData]);

  // Chart data preparation with null safety
  const chartData = useMemo(() => {
    if (!data?.comparison || !Array.isArray(data.comparison) || data.comparison.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'Email Count',
          data: [],
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          borderWidth: 1
        }]
      };
    }
    
    return {
      labels: data.comparison.map(brand => brand.brand),
      datasets: [{
        label: 'Email Count',
        data: data.comparison.map(brand => brand.total_emails || 0),
        backgroundColor: '#3b82f6',
        borderColor: '#2563eb',
        borderWidth: 1
      }]
    };
  }, [data?.comparison]);

  // Time-series data for proper "Campaign Performance Over Time" chart
  const [timeSeriesData, setTimeSeriesData] = useState({
    labels: [],
    datasets: []
  });

  // Load time-series data when selected brands change
  const loadTimeSeriesData = useCallback(async () => {
    // console.log('loadTimeSeriesData called with selectedBrands:', selectedBrands);
    if (!selectedBrands || selectedBrands.length === 0) {
      // console.log('No brands selected, clearing time-series data');
      setTimeSeriesData({ labels: [], datasets: [] });
      return;
    }

    try {
      // console.log('Loading time-series data for brands:', selectedBrands);
      const headers = await getAuthHeaders();
      const brandsParam = selectedBrands.join(',');
      // Use 'all' for all time, or calculate days from date range
      const daysParam = useAllTime ? 'all' : Math.ceil((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24));
      const url = `${getApiUrl('api/analytics/time-series')}?brands=${encodeURIComponent(brandsParam)}&days=${daysParam}&_t=${Date.now()}`;
      // console.log('Time-series API URL:', url);
      
      const response = await fetch(url, {
        headers
      });
      
      // console.log('Time-series response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      // console.log('Time-series data received:', data);
      setTimeSeriesData(data);
    } catch (error) {
      console.error('Error loading time-series data:', error);
      setTimeSeriesData({ labels: [], datasets: [] });
    }
  }, [selectedBrands, useAllTime, dateRange, getAuthHeaders, getApiUrl]);

  // Load time-series data when selected brands change
  useEffect(() => {
    loadTimeSeriesData();
  }, [loadTimeSeriesData]);

  // Psychological trigger radar chart data
  const psychologicalData = useMemo(() => {
    if (!data?.comparison || !Array.isArray(data.comparison) || data.comparison.length === 0) {
      return {
        labels: ['Urgency', 'Scarcity', 'Social Proof', 'Authority', 'Reciprocity'],
        datasets: []
      };
    }
    
    const colors = [
      { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 1)' }, // Blue
      { bg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 1)' }, // Green
      { bg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 1)' }, // Orange
      { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 1)' }, // Red
      { bg: 'rgba(139, 92, 246, 0.2)', border: 'rgba(139, 92, 246, 1)' }  // Purple
    ];
    
    return {
      labels: ['Urgency', 'Scarcity', 'Social Proof', 'Authority', 'Reciprocity'],
      datasets: data.comparison.map((brand, index) => {
        const triggers = brand.psychological_triggers || {};
        const color = colors[index % colors.length];
        
        return {
          label: brand.brand,
          data: [
            Math.round((triggers.urgency || 0) * 100), // Convert to percentage
            Math.round((triggers.scarcity || 0) * 100),
            Math.round((triggers.social_proof || 0) * 100),
            Math.round((triggers.authority || 0) * 100),
            Math.round((triggers.reciprocity || 0) * 100)
          ],
          backgroundColor: color.bg,
          borderColor: color.border,
          borderWidth: 2,
          pointBackgroundColor: color.border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: color.border
        };
      })
    };
  }, [data?.comparison]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false }
    },
    scales: {
      r: { 
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true,
        position: 'top'
      },
      title: { display: false }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#2c3e50'
        }
      },
      y: { 
        beginAtZero: true,
        title: {
          display: true,
          text: 'Email Count',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#2c3e50'
        }
      }
    }
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true,
        position: 'top'
      },
      title: { 
        display: true,
        text: 'Psychological Trigger Analysis',
        font: { size: 16, weight: 'bold' }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value) {
            return value + '%';
          }
        },
        pointLabels: {
          font: { size: 12 }
        }
      }
    }
  };

  // Show loading state
  if (loading && !data) {
    return (
      <div className="enhanced-analytics">
        <div className="loading-spinner">Loading analytics data...</div>
      </div>
    );
  }

  // Show auth required message
  if (!user) {
    return (
      <div className="enhanced-analytics">
        <div className="error-message">
          Please log in to access the analytics dashboard.
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-analytics">
      {/* Research Workspace Header */}
      <div className="research-header">
        <h1>Enhanced Research Tool</h1>
        <p>Advanced analytics and research capabilities for email marketing insights</p>
      </div>

      {/* Research Session Management */}
      <ResearchSessionManager
        selectedBrands={selectedBrands}
        dateRange={dateRange}
        useAllTime={useAllTime}
        researchNotes={researchNotes}
        onSessionLoad={handleSessionLoad}
        onSessionSave={handleSessionSave}
      />

      {/* Enhanced Search Interface */}
      <div className="search-section">
        <h3>Brand Research & Analysis</h3>
        
        <div className="search-controls">
          <div className="search-input-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search for brands..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setSuggestions([])}
            />
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.displayName || suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <button className="search-button" onClick={handleSearch}>
            Analyze Brands
          </button>
          
          <button 
            className="advanced-toggle" 
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          >
            {showAdvancedSettings ? 'Hide' : 'Show'} Advanced Settings
          </button>
        </div>

        {/* Advanced Settings */}
        {showAdvancedSettings && (
          <div className="advanced-settings">
            <h4>Date Range Filter</h4>
            <div className="date-controls">
              <label className="all-time-checkbox">
                <input
                  type="checkbox"
                  checked={useAllTime}
                  onChange={(e) => setUseAllTime(e.target.checked)}
                />
                All Time
              </label>
              
              {!useAllTime && (
                <>
                  <label>
                    Start Date:
                    <input
                      type="date"
                      className="date-input"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </label>
                  
                  <label>
                    End Date:
                    <input
                      type="date"
                      className="date-input"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        )}

        {/* Selected Brands */}
        {selectedBrands.length > 0 && (
          <div className="brand-tags">
            {selectedBrands.map((brand, index) => (
              <div key={index} className="brand-tag">
                {brand.replace(/\.com$/, '').replace(/\./, ' ').split(' ').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
                <button
                  className="remove-brand"
                  onClick={() => handleRemoveBrand(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interactive Charts */}
      {data && (
        <div className="charts-section">
          <h3>Data Visualization</h3>
          
          <div className="chart-container">
            <h4 className="chart-title">Brand Comparison - Email Volume</h4>
            <div className="chart-wrapper">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
          
          <div className="chart-container">
            <h4 className="chart-title">Email Volume Over Time</h4>
            <div className="chart-wrapper">
              {/* console.log('Rendering time-series chart with data:', timeSeriesData) */}
              {timeSeriesData.labels.length === 0 ? (
                <div className="no-data-message">
                  <p>No time-series data available for selected brands.</p>
                  <p>This may be because some brands have missing date information.</p>
                </div>
              ) : (
                <Line data={timeSeriesData} options={lineChartOptions} />
              )}
            </div>
          </div>

          <div className="chart-container">
            <h4 className="chart-title">Psychological Trigger Analysis</h4>
            <div className="chart-wrapper radar-chart">
              <Radar data={psychologicalData} options={radarChartOptions} />
            </div>
            <div className="chart-insights">
              {data.comparison && data.comparison.length > 0 && (
                <div className="insights-grid">
                  {data.comparison.map((brand, index) => {
                    const triggers = brand.psychological_triggers || {};
                    const dominantTrigger = Object.entries({
                      urgency: triggers.urgency || 0,
                      scarcity: triggers.scarcity || 0,
                      social_proof: triggers.social_proof || 0,
                      authority: triggers.authority || 0,
                      reciprocity: triggers.reciprocity || 0
                    }).reduce((a, b) => a[1] > b[1] ? a : b);
                    
                    return (
                      <div key={index} className="brand-insight">
                        <h5>{brand.brand}</h5>
                        <p><strong>Strategy:</strong> {dominantTrigger[0].replace('_', ' ')} focused ({(dominantTrigger[1] * 100).toFixed(0)}%)</p>
                        <p><strong>Sentiment:</strong> {triggers.sentiment > 0.1 ? 'Positive' : triggers.sentiment < -0.1 ? 'Negative' : 'Neutral'} ({(triggers.sentiment * 100)?.toFixed(0) || '0'}%)</p>
                        <p><strong>Sample Size:</strong> {triggers.sample_size || 0} emails</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Research Notes */}
      <div className="research-notes">
        <h3>Research Notes</h3>
        <textarea
          className="notes-textarea"
          placeholder="Add your research notes, insights, and observations here..."
          value={researchNotes}
          onChange={(e) => setResearchNotes(e.target.value)}
        />
        <div className="notes-actions">
          <button className="save-notes" onClick={handleSaveNotes}>
            Save Notes
          </button>
          <button className="clear-notes" onClick={handleClearNotes}>
            Clear Notes
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default EnhancedAnalytics;
